import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../ui/Toast'
import { useConfirm } from '../ui/ConfirmDialog'
import Modal from '../ui/Modal'
import Button from '../ui/Button'
import { MoneyInput } from '../ui/Input'
import { fmtS, dateStr, uid } from '../../utils/formatters'
import { exportToExcel } from '../../utils/exportExcel'
import { SkeletonStats, SkeletonList } from '../ui/Skeleton'

const STATUS_OPTIONS = [
  { value: 'planning', label: '📋 Perencanaan' },
  { value: 'active', label: '🏗️ Aktif' },
  { value: 'on-hold', label: '⏸️ Ditunda' },
  { value: 'completed', label: '✅ Selesai' },
]
const STATUS_BADGES = { planning: 'badge-info', active: 'badge-accent', 'on-hold': 'badge-warning', completed: 'badge-success' }

export default function Projects() {
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [show, setShow] = useState(false)
  const [edit, setEdit] = useState(null)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')

  const blank = { name: '', client: '', location: '', start_date: new Date().toISOString().split('T')[0], end_date: '', status: 'active', progress: 0, budget: 0, team: '' }
  const [form, setForm] = useState(blank)

  useEffect(() => { if (!user) return; fetchProjects() }, [user])

  const fetchProjects = async () => {
    setLoading(true)
    const { data } = await supabase.from('projects').select('*').eq('user_id', user.id).order('created_at', { ascending: false })
    if (data) setProjects(data)
    setLoading(false)
  }

  const save = async () => {
    if (!form.name) { toast.show('Nama proyek wajib diisi', 'error'); return }
    const data = { ...form, budget: parseInt(form.budget), progress: parseInt(form.progress), user_id: user.id }
    if (edit) {
      await supabase.from('projects').update(data).eq('id', edit)
    } else {
      await supabase.from('projects').insert([{ ...data, id: uid() }])
    }
    setShow(false)
    toast.show(edit ? 'Proyek diperbarui' : 'Proyek ditambahkan')
    fetchProjects()
  }

  const del = async (id, name) => {
    const ok = await confirm(`Hapus proyek "${name}"? Semua data terkait (RAB, laporan, dll) akan ikut terhapus.`, {
      title: 'Hapus Proyek', icon: '🏗️', confirmLabel: 'Hapus', confirmVariant: 'danger'
    })
    if (!ok) return
    await supabase.from('projects').delete().eq('id', id)
    toast.show('Proyek dihapus')
    fetchProjects()
  }

  const filtered = projects.filter(p => {
    const matchS = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.client?.toLowerCase().includes(search.toLowerCase())
    const matchSt = filterStatus === 'all' || p.status === filterStatus
    return matchS && matchSt
  })

  if (loading) return <div><SkeletonStats cols={4} /><SkeletonList count={3} /></div>

  return (
    <div>
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        {[
          { val: projects.filter(p => p.status === 'active').length, label: 'Aktif', color: 'var(--accent)' },
          { val: projects.filter(p => p.status === 'completed').length, label: 'Selesai', color: 'var(--success)' },
          { val: fmtS(projects.reduce((s, p) => s + (p.budget || 0), 0)), label: 'Total Nilai', color: 'var(--info)' },
          { val: `${projects.length ? Math.round(projects.reduce((s, p) => s + (p.progress || 0), 0) / projects.length) : 0}%`, label: 'Avg Progress', color: 'var(--text-primary)' },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div className="stat-value" style={{ color: s.color }}>{s.val}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="filter-row">
        <div className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input placeholder="Cari proyek atau klien..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 150 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">Semua Status</option>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Button variant="secondary" onClick={() => { exportToExcel(filtered.map(p => ({ 'Nama': p.name, 'Klien': p.client, 'Status': p.status, 'Progress': `${p.progress}%`, 'Nilai': p.budget })), 'Data-Proyek'); toast.show('Excel diunduh') }}>📊</Button>
        <Button onClick={() => { setForm(blank); setEdit(null); setShow(true) }}>➕ Proyek</Button>
      </div>

      {filtered.map(p => (
        <div key={p.id} className="mobile-card-item">
          <div className="mc-header">
            <div>
              <div className="mc-title">{p.name}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>📍 {p.location || '-'} • 👤 {p.client || '-'}</div>
            </div>
            <span className={`badge ${STATUS_BADGES[p.status]}`}>{STATUS_OPTIONS.find(o => o.value === p.status)?.label.split(' ')[1] || p.status}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 10 }}>Progress</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: p.progress >= 70 ? 'var(--success)' : p.progress >= 40 ? 'var(--accent)' : 'var(--warning)' }}>{p.progress}%</span>
          </div>
          <div className="progress-bar" style={{ marginBottom: 8 }}>
            <div className="fill" style={{ width: `${p.progress}%`, background: p.progress >= 70 ? 'var(--success)' : p.progress >= 40 ? 'var(--accent)' : 'var(--warning)' }} />
          </div>
          <div className="mc-row"><span className="mc-label">Nilai Proyek</span><span className="mc-value" style={{ color: 'var(--accent)' }}>{fmtS(p.budget)}</span></div>
          <div className="mc-row"><span className="mc-label">Periode</span><span className="mc-value">{dateStr(p.start_date)} – {dateStr(p.end_date) || '?'}</span></div>
          {p.team && <div className="mc-row"><span className="mc-label">Tim</span><span className="mc-value">{p.team}</span></div>}
          <div className="mc-actions">
            <Button size="sm" variant="secondary" onClick={() => { setForm(p); setEdit(p.id); setShow(true) }}>✏️ Edit</Button>
            <Button size="sm" variant="danger" onClick={() => del(p.id, p.name)}>🗑️ Hapus</Button>
          </div>
        </div>
      ))}

      {!filtered.length && (
        <div className="empty-state">
          <div className="empty-icon">🏗️</div>
          <p>{search || filterStatus !== 'all' ? 'Tidak ada proyek yang cocok' : 'Belum ada proyek'}</p>
          <Button variant="primary" style={{ marginTop: 12 }} onClick={() => { setForm(blank); setEdit(null); setShow(true) }}>➕ Tambah Proyek</Button>
        </div>
      )}

      <Modal show={show} onClose={() => setShow(false)} title={edit ? '✏️ Edit Proyek' : '➕ Tambah Proyek'}
        footer={<><Button variant="secondary" onClick={() => setShow(false)}>Batal</Button><Button onClick={save}>💾 Simpan</Button></>}
      >
        <div className="form-group">
          <label className="form-label">Nama Proyek *</label>
          <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Cth: Renovasi Gedung Kantor A" />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Klien</label>
            <input className="form-input" value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Lokasi</label>
            <input className="form-input" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Tgl Mulai</label>
            <input className="form-input" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Tgl Selesai</label>
            <input className="form-input" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Nilai Proyek</label>
          <MoneyInput value={form.budget} onChange={v => setForm({ ...form, budget: v })} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Progress (%)</label>
            <input className="form-input" type="number" min="0" max="100" value={form.progress} onChange={e => setForm({ ...form, progress: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Nama Tim</label>
          <input className="form-input" value={form.team} onChange={e => setForm({ ...form, team: e.target.value })} placeholder="Cth: Tim A" />
        </div>
      </Modal>
    </div>
  )
}